export default function WichitaFallsTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Wichita Falls Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Wichita Falls travelers actually move helps us anticipate the passport urgencies that arise here. From Sheppard AFB PCS cycles to Midwestern State study-abroad seasons, North Texas travel operates on a distinct rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">DFW — Dallas/Fort Worth International Airport</h3>
                <p className="text-gray-400 text-xs">About 120 miles south — 2-hour drive from Wichita Falls</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Wichita Falls travelers routinely drive to DFW for international departures. The airport offers nonstop service to international hubs across Europe, Asia, Latin America, and the Middle East — making it the primary gateway for North Texas international travel. Military families, business professionals, and MSU students all funnel through DFW for international trips, often with discovery of passport issues happening close to departure.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking a DFW international departure. Many countries require six months validity beyond your return date — and that two-hour drive to DFW can become a wasted trip if you arrive with an expired passport.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-shield-star-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Sheppard AFB PCS &amp; Deployment Cycles</h3>
                <p className="text-gray-400 text-xs">82nd Training Wing — NATO Pilot Training Program</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Sheppard AFB hosts one of the few remaining multi-nation NATO pilot training programs in the US, meaning its international community is uniquely diverse and passport-dependent. PCS orders frequently arrive with 30 days or less to report to overseas assignments. Dependent passports must be current for OCONUS duty stations. We regularly process expedited passports for Sheppard families on compressed PCS timelines — including infant passports for newborns before an overseas move.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check every dependent&apos;s passport expiration as soon as PCS orders arrive — not the week before your report date. Dependent passports must have at least six months validity for most overseas assignments.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Midwestern State University Study Abroad</h3>
                <p className="text-gray-400 text-xs">MSU Texas — fall and spring semester programs</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              MSU Texas sends students abroad each year for semester exchanges, summer programs, and faculty-led international courses. Study-abroad application deadlines often fall in October and February, but many students do not think about their passport until after acceptance. Standard post office processing frequently exceeds program deadlines, making expedited service essential. First-time applicants (many MSU students) require an in-person acceptance clerk visit — we guide them through the exact steps.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Apply for your passport the moment you apply for a study-abroad program — not after acceptance. Processing times can easily exceed orientation deadlines for standard mail-in applications.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-briefcase-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Energy Sector &amp; Business Travel</h3>
                <p className="text-gray-400 text-xs">Oil &amp; gas, healthcare, and professional services</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Wichita Falls sits in the broader Permian Basin energy corridor, and its business community travels internationally for energy sector conferences, equipment sourcing, and project oversight in oil-producing nations. Healthcare professionals at United Regional and other facilities travel internationally for conferences and specialty training. These travelers often book international trips on short notice when opportunities arise, then discover passport issues at the worst possible time.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Energy and healthcare professionals should renew passports proactively — at the 3-year mark rather than waiting for expiration. International opportunities in these sectors arise quickly and rarely wait for government processing timelines.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}